import React from "react";
import { computeSquaresTopPrizeFirstPlaceCoins, SQUARES_OVER_HEADER_META_TEXT_CLASS, SQUARES_OVER_HEADER_META_TEXT_RIGHT_CLASS } from "./squaresConstants";

const SIZE_CLASSES = {
  default: "text-[8px] sm:text-[9px]",
  /** Match play over header: players · prize (right-aligned). */
  matchPlayHeader: SQUARES_OVER_HEADER_META_TEXT_RIGHT_CLASS,
};

const IMG_SIZE_CLASSES = {
  default: "h-3.5 w-3.5 sm:h-4 sm:w-4",
  matchPlayHeader: "h-[18px] w-[18px] lg:h-[20px] lg:w-[20px]",
};

/**
 * 1st-place prize label + amount: prefers `top_prize` from API when set; else from `pool_entrant_count`
 * like backend `computeTopPrizeFirstPlaceFromN`. Explicit `top_prize: 0` (league Super Squares) hides
 * the coin prize but still shows player count in the match-play header.
 */
export function SquaresPrizePoolBadge({ econ, className = "", size = "default", entrantCount: entrantCountProp }) {
  const tp = econ?.top_prize;
  let top = null;
  if (tp != null && Number.isFinite(Number(tp))) {
    // 0 = points-model / no shared pot — do not invent a public 1st-place prize from pool size.
    top = Number(tp) > 0 ? Math.round(Number(tp)) : null;
  } else {
    top = computeSquaresTopPrizeFirstPlaceCoins(econ?.pool_entrant_count);
  }
  const isHeader = size === "matchPlayHeader";
  const sizeCls = SIZE_CLASSES[size] ?? SIZE_CLASSES.default;
  const imgCls = IMG_SIZE_CLASSES[size] ?? IMG_SIZE_CLASSES.default;

  const entrantRaw =
    entrantCountProp != null && Number.isFinite(Number(entrantCountProp))
      ? Math.max(0, Math.floor(Number(entrantCountProp)))
      : econ?.pool_entrant_count != null && Number.isFinite(Number(econ.pool_entrant_count))
        ? Math.max(0, Math.floor(Number(econ.pool_entrant_count)))
        : null;

  if (isHeader) {
    const playersLabel =
      entrantRaw != null ? `${entrantRaw} ${entrantRaw === 1 ? "PLAYER" : "PLAYERS"}` : null;
    if (playersLabel == null && top == null) return null;
    return (
      <span
        className={`inline-flex max-w-full flex-nowrap items-center justify-end gap-x-1 whitespace-nowrap ${sizeCls} ${className}`.trim()}
      >
        {playersLabel != null && (
          <>
            <span className="shrink-0">{playersLabel}</span>
            {top != null ? (
              <span
                className={`shrink-0 opacity-30 ${SQUARES_OVER_HEADER_META_TEXT_CLASS} text-right`}
                aria-hidden
              >
                ·
              </span>
            ) : null}
          </>
        )}
        {top != null ? (
          <span className="inline-flex min-w-0 shrink items-center justify-end gap-0.5">
            <span className="shrink-0">1ST PLACE:</span>
            <span className="tabular-nums shrink-0">{top}</span>
            <img
              src="/assets/currency-gold-ball.png"
              alt=""
              className={`shrink-0 object-contain opacity-95 ${imgCls}`.trim()}
              aria-hidden
            />
          </span>
        ) : null}
      </span>
    );
  }

  if (top == null) return null;

  const tone = "text-orange-400";
  return (
    <span
      className={`inline-flex flex-nowrap items-center gap-x-1 whitespace-nowrap font-anton uppercase tracking-wide ${sizeCls} ${tone} ${className}`.trim()}
    >
      <span className="text-current opacity-80">1st place</span>
      <span className="inline-flex items-center gap-0.5">
        <img
          src="/assets/currency-gold-ball.png"
          alt=""
          className={`shrink-0 object-contain opacity-95 ${imgCls}`.trim()}
          aria-hidden
        />
        <span className="tabular-nums text-current">{top}</span>
      </span>
    </span>
  );
}
